/**
 * SSE Service - Default Implementation
 *
 * Manages SSE connection lifecycle using native EventSource.
 * Reconnects automatically when auth state changes.
 * Uses session cookie for authentication (browser sends automatically).
 *
 * Uses a single onmessage handler to catch all events.
 * Server embeds event type in data payload: { type: "...", data: {...} }
 * This simplifies reconnection (only one listener to manage).
 */

import { inject } from "../../di";
import { appContext } from "../../appContext";
import type { IWritableSseState, ISseService } from "./types";

export class SseService implements ISseService {
  @inject<IWritableSseState>("writableSseState")
  private sseState!: IWritableSseState;

  private eventSource: EventSource | null = null;

  /**
   * Internal listener registry.
   * Maps event type → Set of handlers for that event.
   */
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  initialize(): void {
    // Skip SSE in test environment (allows waitForLoadState("networkidle") to work)
    if (typeof window !== "undefined" && (window as Window & { __DISABLE_SSE__?: boolean }).__DISABLE_SSE__) {
      console.log("[SSE] Disabled for testing");
      return;
    }

    this.setupAuthListeners();
    this.connect();
  }

  dispose(): void {
    this.disconnect();
    this.listeners.clear();
  }

  private setupAuthListeners(): void {
    const originalSignIn = appContext.onSingInCallback;
    const originalSignOut = appContext.onSignOutCallback;

    appContext.onSingInCallback = (user) => {
      originalSignIn(user);
      console.log("[SSE] User signed in, reconnecting");
      this.connect();
    };

    appContext.onSignOutCallback = () => {
      originalSignOut();
      console.log("[SSE] User signed out, reconnecting");
      this.connect();
    };
  }

  private getSseUrl(): string {
    if (typeof window === "undefined" || window.location.hostname === "localhost") {
      return "/sse/";
    }
    return `https://sse.${window.location.hostname}/`;
  }

  private connect(): void {
    this.disconnect();
    this.sseState.status.set("connecting");

    // Connect to SSE via custom domain (bypasses Front Door which doesn't support SSE)
    this.eventSource = new EventSource(this.getSseUrl(), { withCredentials: true });

    this.eventSource.onopen = () => {
      this.sseState.status.set("connected");
      console.log("[SSE] Connected");
    };

    this.eventSource.onerror = () => {
      this.sseState.status.set("disconnected");
      console.log("[SSE] Disconnected, will auto-reconnect...");
    };

    // Single catch-all handler for ALL events
    // Server sends: data: { type: "event-type", data: {...} }
    this.eventSource.onmessage = (event) => {
      try {
        const { type, data } = JSON.parse(event.data) as { type: string; data: unknown };
        this.handleEvent(type, data);
      } catch (error) {
        console.error("[SSE] Failed to parse event:", error);
      }
    };
  }

  private disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Route event to appropriate handlers based on type.
   * Infrastructure events (connected, heartbeat) handled internally.
   * Feature events forwarded to registered listeners.
   */
  private handleEvent(type: string, data: unknown): void {
    // Infrastructure events
    if (type === "connected") {
      const { hasSession } = data as { hasSession?: boolean };
      this.sseState.hasSession.set(hasSession ?? false);
      console.log("[SSE] Session:", hasSession ? "authenticated" : "anonymous");
      return;
    }

    if (type === "heartbeat") {
      console.log("[SSE] heartbeat");
      return;
    }

    // Feature events - forward to listeners
    this.notifyListeners(type, data);
  }

  /**
   * Subscribe to an SSE event type.
   *
   * @param eventType - The SSE event type (e.g., "live-chat-message")
   * @param handler - Callback receiving the parsed event data
   * @returns Unsubscribe function
   */
  on<T = unknown>(eventType: string, handler: (data: T) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(handler as (data: unknown) => void);

    return () => {
      this.listeners.get(eventType)?.delete(handler as (data: unknown) => void);
    };
  }

  /**
   * Notify all handlers subscribed to an event type.
   */
  private notifyListeners(eventType: string, data: unknown): void {
    const handlers = this.listeners.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }
}
